import { action, computed, observable } from 'mobx'

import { PermitActionType, PermitFieldType } from '~/client/graph'
import {
  IEndClosureAction,
  IPermitTypeField,
  IStartClosureAction,
  IWorkflowStep,
  WorkflowStepType,
} from '~/client/graph/types.generated'
import { IWorkflowTypeAction } from '~/client/src/shared/models/WorkflowType'

import WorkflowStepConfiguratorStore from '../WorkflowStepConfigurator.store'

export interface IFieldArgumentOption {
  stepName?: string
  stepPosition?: number
  field?: IPermitTypeField
  fieldPosition?: number
  subFieldPosition?: number
  isDisabled?: boolean
  isConditional?: boolean
  shouldIndent?: boolean
}

export default class WorkflowActionConfiguratorStore {
  @observable public currentStepId: string = ''
  private stepConfiguratorStore: WorkflowStepConfiguratorStore

  public constructor(
    private readonly changeActionsCallback: (
      actions: IWorkflowTypeAction[],
    ) => void,
  ) {}

  @action.bound
  public initialize(
    stepConfiguratorStore: WorkflowStepConfiguratorStore,
    stepId: string,
  ) {
    this.stepConfiguratorStore = stepConfiguratorStore
    this.currentStepId = stepId
  }

  public updateAction = (updatedAction: IWorkflowTypeAction) => {
    if (!this.currentStep) return

    const currentActions = this.getCurrentStepActions()
    const updatedActions = currentActions.map(action =>
      action.type === updatedAction.type ? updatedAction : action,
    )

    this.changeActionsCallback(updatedActions)
  }

  public removeAction = (actionIndex: number) => {
    if (!this.currentStep) return

    const currentActions = this.getCurrentStepActions()
    const actionToRemove = currentActions[actionIndex]

    if (
      this.isLastStep &&
      actionToRemove.type === PermitActionType.EndClosureAction &&
      this.hasStartClosureActionInAnyStep
    ) {
      return
    }

    const newActions = [...currentActions]
    newActions.splice(actionIndex, 1)

    this.changeActionsCallback(newActions)
  }

  public createAction = (action: IWorkflowTypeAction) => {
    if (!this.currentStep) return

    if (!this.canAddAction(action.type)) {
      return
    }

    const currentActions = this.getCurrentStepActions()
    const newActions = [...currentActions, action]

    this.changeActionsCallback(newActions)
  }

  public getFieldArgumentOptions(
    fieldTypes: PermitFieldType[],
    stepId: string,
  ): IFieldArgumentOption[] {
    if (!this.stepConfiguratorStore) return []
    return this.stepConfiguratorStore.getNotificationOptions(stepId, fieldTypes)
  }

  private getCurrentStepActions(): IWorkflowTypeAction[] {
    if (!this.currentStep) return []
    return this.getStepActions(this.currentStepId)
  }

  private getStepActions(stepId: string): IWorkflowTypeAction[] {
    if (!this.stepConfiguratorStore) return []

    const step = this.stepConfiguratorStore.workflowSteps.find(
      s => s.id === stepId,
    )
    if (!step) return []

    return (step.actions as IWorkflowTypeAction[]) || []
  }

  @computed
  public get currentStep(): IWorkflowStep | undefined {
    if (!this.stepConfiguratorStore) return undefined
    return this.stepConfiguratorStore.workflowSteps.find(
      step => step.id === this.currentStepId,
    )
  }

  @computed
  public get hasStartClosureActionInAnyStep(): boolean {
    if (!this.stepConfiguratorStore) return false

    return this.stepConfiguratorStore.workflowSteps.some(step => {
      const stepActions = (step.actions as IWorkflowTypeAction[]) || []
      return stepActions.some(
        action => action.type === PermitActionType.StartClosureAction,
      )
    })
  }

  @computed
  public get hasStartClosureActionInCurrentStep(): boolean {
    const currentActions = this.getCurrentStepActions()
    return currentActions.some(
      action => action.type === PermitActionType.StartClosureAction,
    )
  }

  @computed
  public get isLastStep(): boolean {
    if (!this.stepConfiguratorStore || !this.currentStepId) return false

    const workflowSteps = this.stepConfiguratorStore.workflowSteps
    if (!workflowSteps || workflowSteps.length === 0) return false

    const lastStepId = workflowSteps[workflowSteps.length - 1].id
    return this.currentStepId === lastStepId
  }

  public canAddAction(actionType: PermitActionType): boolean {
    if (
      actionType === PermitActionType.StartClosureAction &&
      (this.hasStartClosureActionInAnyStep || this.isLastStep)
    )
      return false

    if (actionType === PermitActionType.EndClosureAction) return false

    if (this.currentStep?.type === WorkflowStepType.RecurringInspection)
      return false

    return true
  }

  public canRemoveAction(actionType: PermitActionType): boolean {
    return actionType !== PermitActionType.EndClosureAction
  }

  public getAvailableActionTypes(): PermitActionType[] {
    const availableTypes: PermitActionType[] = []

    if (!this.hasStartClosureActionInAnyStep && !this.isLastStep) {
      availableTypes.push(PermitActionType.StartClosureAction)
    }

    return availableTypes
  }

  public createNewAction(actionType: PermitActionType): IWorkflowTypeAction {
    switch (actionType) {
      case PermitActionType.StartClosureAction:
        return {
          type: PermitActionType.StartClosureAction,
          locationFieldsMap: [],
        } as IStartClosureAction

      case PermitActionType.EndClosureAction:
        return {
          type: PermitActionType.EndClosureAction,
          locationFieldIds: [],
        } as IEndClosureAction

      default:
        throw new Error(`Unsupported action type: ${actionType}`)
    }
  }

  public getNewAvailableActions(): IWorkflowTypeAction[] {
    return Object.values(PermitActionType)
      .filter(type => this.canAddAction(type))
      .map(type => this.createNewAction(type))
  }
}
